import axios from 'axios';

import { NotificationData } from '../types/notification';

const WS_URL = 'wss://notification.api.delfi.lt/notification/v2/subscriptions';

export interface NotificationState {
  unreadCount: number;
  showProfileDot: boolean;
}

type NotificationListener = (state: NotificationState) => void;

interface NotificationSubscriptionPayload {
  event: 'NOTIFICATION_CREATED' | 'NOTIFICATION_PUBLISHED' | 'NOTIFICATION_UPDATED';
  notification: {
    id: string;
    status: 'NOT_SEEN' | 'SEEN';
  };
}

interface NotificationWindow extends Window {
  tp: any;
}

declare const window: NotificationWindow;

class NotificationService {
  private socket: WebSocket | null = null;
  private listeners = new Set<NotificationListener>();
  private reconnectTimer: number | null = null;
  private unreadCount = 0;
  private showProfileDot = false;
  private initialized = false;
  private connecting = false;
  private customerToken = '';

  private readonly query = `
    query portal_root_getNotifications($input: GetNotificationsInput!) {
      data: getNotifications(input: $input) {
        items {
          id
          status
        }
      }
    }
  `;

  setCustomerToken(token: string) {
    this.customerToken = token;
  }

  async init(): Promise<NotificationState> {
    if (!window.tp?.user?.isUserValid?.()) {
      return this.getState();
    }

    if (this.initialized || this.connecting) {
      return this.getState();
    }

    this.connecting = true;

    try {
      if (!this.customerToken) {
        this.customerToken = await this.getToken();
      }

      await this.loadUnreadCount();
      await this.connect();

      this.initialized = true;
    } finally {
      this.connecting = false;
    }

    return this.getState();
  }

  subscribe(listener: NotificationListener): () => void {
    this.listeners.add(listener);
    listener(this.getState());

    return () => {
      this.listeners.delete(listener);
    };
  }

  hideProfileDot(): void {
    if (!this.showProfileDot) {
      return;
    }

    this.showProfileDot = false;
    this.emit();
  }

  destroy(): void {
    this.initialized = false;

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    this.socket?.close();
    this.socket = null;
  }

  async getNotifications(): Promise<NotificationData[]> {
    const token = this.customerToken || (await this.getToken());

    const response = await axios.get('/api/get-notifications', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return response.data.data || [];
  }

  async updateNotification(id: string) {
    const token = this.customerToken || (await this.getToken());

    return axios.post(
      '/api/update-notification',
      {
        input: {
          id,
          status: 'SEEN',
          type: 'WEB',
        },
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
  }

  private getState(): NotificationState {
    return {
      unreadCount: this.unreadCount,
      showProfileDot: this.showProfileDot,
    };
  }

  private emit(): void {
    const state = this.getState();
    this.listeners.forEach((listener) => listener(state));
  }

  private async getToken(): Promise<string> {
    return window.tp.pianoId.getToken();
  }

  private async loadUnreadCount(): Promise<void> {
    const token = this.customerToken || (await this.getToken());

    const response = await fetch('/api/notifications/unread', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      return;
    }

    const data = await response.json();

    this.unreadCount = data.unreadCount;
    this.showProfileDot = this.unreadCount > 0;
    this.emit();
  }

  private async connect(): Promise<void> {
    const token = this.customerToken || (await this.getToken());

    this.socket = new WebSocket(WS_URL, 'graphql-transport-ws');

    this.socket.onopen = () => {
      this.socket?.send(
        JSON.stringify({
          type: 'connection_init',
          payload: {
            Authorization: `Bearer ${token}`,
          },
        })
      );
    };

    this.socket.onmessage = async (event) => {
      const message = JSON.parse(event.data);

      switch (message.type) {
        case 'connection_ack':
          this.subscribeNotifications();
          return;

        case 'next': {
          const payload: NotificationSubscriptionPayload | undefined = message.payload?.data?.notificationSubscription;

          if (!payload) {
            return;
          }

          await this.loadUnreadCount();
          break;
        }

        default:
          break;
      }
    };

    this.socket.onerror = (error) => {
      console.error(error);
    };
  }

  private subscribeNotifications(): void {
    this.socket?.send(
      JSON.stringify({
        id: 'notifications',
        type: 'subscribe',
        payload: {
          query: `
            subscription {
              notificationSubscription(input:{type:[WEB]}) {
                event
                notification {
                  id
                  status
                }
              }
            }
          `,
        },
      })
    );
  }
}

export default new NotificationService();
